import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { resolveImageUrl } from '../lib/api';
import { useLang } from '../hooks/useLang';
import { pickLocalized } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { lang, routes } = useLang();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/projects/${slug}`);
        if (!response.ok) throw new Error('Project not found');
        const data = await response.json();
        setProject(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 px-8 lg:px-16"><p className="text-center text-gray-500">{t('common.loading')}</p></div>
        <Footer />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 px-8 lg:px-16">
          <p className="text-center text-gray-500">{t('projects.notFound')}</p>
          <div className="mt-8 text-center">
            <Link to={routes.home} className="text-[#d94e47] hover:underline">{t('common.backToHome')}</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const title = pickLocalized(project, 'title', lang);
  const category = pickLocalized(project, 'category', lang);
  const location = pickLocalized(project, 'location', lang);
  const description = pickLocalized(project, 'description', lang) || [];
  const infoItems = (project.info || []).map((i) => ({
    label: (lang === 'en' && i.label_en) ? i.label_en : i.label,
    value: (lang === 'en' && i.value_en) ? i.value_en : i.value,
  }));
  const images = (project.images || []).map((img) => ({
    url: img.url,
    caption: (lang === 'en' && img.caption_en) ? img.caption_en : img.caption,
  }));

  const openLightbox = (index) => { setCurrentImageIndex(index); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-32 pb-16 px-8 lg:px-16 bg-[#f5f2ed]">
        <div className="max-w-[1920px] mx-auto">
          <Link to={routes.projects} className="inline-flex items-center gap-2 text-sm font-light text-gray-600 hover:text-[#d94e47] transition-colors duration-300 mb-8">
            <ChevronLeft className="w-4 h-4" />
            {t('common.backToProjects')}
          </Link>
          <div className="mb-4">
            <span className="text-xs font-light tracking-wider text-[#d94e47] uppercase">{category}</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-light mb-6 leading-tight">{title}</h1>
          {location && <p className="text-lg font-light text-gray-600 mb-8">{location}</p>}
        </div>
      </section>

      <section className="py-16 px-8 lg:px-16">
        <div className="max-w-[1920px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-light mb-6">{t('projects.description')}</h2>
              <div className="space-y-4 text-base font-light text-gray-700 leading-relaxed">
                {description.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-light mb-6">{t('projects.info')}</h3>
              <dl className="space-y-4">
                {infoItems.map((item, index) => (
                  <div key={index}>
                    <dt className="text-xs font-light tracking-wider text-gray-500 uppercase mb-1">{item.label}</dt>
                    <dd className="text-base font-light text-gray-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-8 lg:px-16 bg-[#f5f2ed]">
        <div className="max-w-[1920px] mx-auto">
          <h2 className="text-3xl font-light mb-12">{t('projects.gallery')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {images.map((image, index) => (
              <div key={index} className="overflow-hidden cursor-pointer group bg-white" onClick={() => openLightbox(index)}>
                <img src={resolveImageUrl(image.url)} alt={image.caption || `${index + 1}`}
                  className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105" />
                {image.caption && <p className="p-4 text-sm font-light text-gray-600">{image.caption}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {lightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center">
          <button onClick={closeLightbox} aria-label={t('common.close')}
            className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center bg-white bg-opacity-10 hover:bg-opacity-20 transition-all duration-300">
            <X className="w-6 h-6 text-white" />
          </button>
          <button onClick={prevImage} aria-label={t('common.previousImage')}
            className="absolute left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white bg-opacity-10 hover:bg-opacity-20 transition-all duration-300">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-[90vw] max-h-[90vh]">
            <img src={resolveImageUrl(images[currentImageIndex].url)} alt={images[currentImageIndex].caption || `${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain" />
            {images[currentImageIndex].caption && (
              <p className="text-center mt-4 text-white text-sm font-light">{images[currentImageIndex].caption}</p>
            )}
          </div>
          <button onClick={nextImage} aria-label={t('common.nextImage')}
            className="absolute right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white bg-opacity-10 hover:bg-opacity-20 transition-all duration-300">
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white text-sm font-light">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProjectDetail;
